import FontAwesome from '@expo/vector-icons/FontAwesome'
import { Stack } from 'expo-router'
import Head from 'expo-router/head'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native'
import Footer from '@/components/Footer'
import PublicHeader from '@/components/PublicHeader'
import { Text } from '@/components/ui/Text'
import YoutubePlayer from '@/components/YoutubePlayer'
import { useThemeColors } from '@/contexts/ThemeContext'
import { serializeJsonForScriptTag } from '@/lib/jsonForScriptTag'

const ABOUT_VIDEO_URL = 'https://www.youtube.com/watch?v=IoDxhus69S8'
const ABOUT_VIDEO_ID = 'IoDxhus69S8'

export default function WhatWeDo() {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const { width } = useWindowDimensions()
  const [scrollOffset, setScrollOffset] = useState(0)
  const isSmallScreen = width < 768
  const videoAspectRatio = isSmallScreen ? 9 / 16 : 16 / 9

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (window.location.hash === '#video') {
        setTimeout(() => {
          const videoElement = document.getElementById('video')
          if (videoElement) {
            videoElement.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      }
    }
  }, [])

  const sections = [
    {
      title: t('about.roadmapTitle'),
      description: t('about.roadmapDesc'),
      image: require('@/assets/about/flight.webp'),
      code: 'FLT',
    },
    {
      title: t('about.savingsTitle'),
      description: t('about.savingsDesc'),
      image: require('@/assets/about/savings.webp'),
      code: 'SAV',
    },
    {
      title: t('about.communityTitle'),
      description: t('about.communityDesc'),
      image: require('@/assets/about/community.webp'),
      code: 'CMT',
    },
  ]

  // Derived border/divider tokens — all from theme, no fixed accent
  const ruleFaint = colors.dark + '14'
  const ruleMid = colors.dark + '35'

  return (
    <View className="flex-1" style={{ backgroundColor: colors.background }}>
      <Head>
        <title>{t('about.pageTitle')}</title>
        <meta name="description" content={t('about.pageDescription')} />
        <link rel="canonical" href="https://turbinago.com/about" />
        <meta property="og:title" content={t('about.pageTitle')} />
        <meta property="og:description" content={t('about.pageDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://turbinago.com/about" />
        <meta property="og:image" content="https://turbinago.com/og-default.png" />
        <meta property="og:site_name" content="Turbina" />
        <meta property="og:locale" content="es_419" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('about.pageTitle')} />
        <meta name="twitter:description" content={t('about.pageDescription')} />
        <meta name="twitter:image" content="https://turbinago.com/og-default.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonForScriptTag({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: t('about.pageTitle'),
              description: t('about.pageDescription'),
              url: 'https://turbinago.com/about',
              isPartOf: {
                '@type': 'WebSite',
                url: 'https://turbinago.com',
                name: 'Turbina',
              },
            }),
          }}
        />
        {Platform.OS === 'web' && (
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .tbn-card { transition: transform 0.22s ease, box-shadow 0.22s ease; }
                .tbn-card:hover { transform: translateY(-2px); }
                .tbn-social { transition: opacity 0.18s ease; cursor: pointer; }
                .tbn-social:hover { opacity: 0.7; }
              `,
            }}
          />
        )}
      </Head>
      <Stack.Screen options={{ title: t('about.pageTitle') }} />
      <PublicHeader scrollOffset={scrollOffset} activeRoute="/about" />

      <ScrollView
        className="flex-1"
        onScroll={(e) => setScrollOffset(e.nativeEvent.contentOffset.y)}
        scrollEventThrottle={16}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View className="flex-1 pt-6 pb-10 max-w-[1200px] self-center w-full">
          <View className="px-6">
            <View className="w-full">
              {/* ─── HERO ─────────────────────────────────────────────── */}
              <View className="mb-14 pt-4">
                {/* Heading block */}
                <View className="items-center gap-3 px-2">
                  <h1
                    className="text-xl md:text-3xl font-bold native:font-bold text-center"
                    style={{
                      color: colors.dark,
                      letterSpacing: -0.5,
                    }}
                  >
                    {t('about.hero')}
                  </h1>
                  <Text
                    className="text-sm text-center leading-relaxed"
                    style={{ color: colors.muted, maxWidth: 460 }}
                  >
                    {t('about.heroSub')}
                  </Text>
                </View>
              </View>

              {/* ─── VIDEO ────────────────────────────────────────────── */}
              <View
                id="video"
                className="mb-10 rounded-2xl overflow-hidden tbn-card"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: ruleMid,
                }}
              >
                {/* Header bar */}
                <View
                  className="flex-row items-center justify-between px-5 py-3"
                  style={{ borderBottomWidth: 1, borderBottomColor: ruleFaint }}
                >
                  <View className="flex-row items-center gap-2">
                    <View
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 4,
                        backgroundColor: '#22C55E',
                      }}
                    />
                    <Text style={{ color: colors.muted, fontSize: 9, letterSpacing: 4 }}>DEMO</Text>
                  </View>
                </View>

                <View className="p-5 gap-3">
                  <Text className="text-xl font-bold" style={{ color: colors.dark }}>
                    {t('about.demoTitle')}
                  </Text>
                  <Text className="text-sm leading-relaxed" style={{ color: colors.muted }}>
                    {t('about.demoDesc')}
                  </Text>
                  <View
                    className="overflow-hidden rounded-xl mt-1"
                    style={{ backgroundColor: colors.background }}
                  >
                    <YoutubePlayer videoId={ABOUT_VIDEO_ID} aspectRatio={videoAspectRatio} />
                  </View>
                  <Pressable
                    className="self-start"
                    onPress={() => Linking.openURL(ABOUT_VIDEO_URL)}
                    accessibilityRole="link"
                  >
                    <Text
                      className="text-sm"
                      style={{ color: colors.primary, textDecorationLine: 'underline' }}
                    >
                      {t('about.watchOnYouTube')}
                    </Text>
                  </Pressable>
                </View>
              </View>

              {/* ─── WHO WE ARE ───────────────────────────────────────── */}
              <View
                className="rounded-2xl overflow-hidden mb-10"
                style={{ backgroundColor: colors.surface }}
              >
                {/* Bold top stripe */}
                <View style={{ height: 3, backgroundColor: colors.dark }} />

                <View className="p-6 gap-5">
                  {/* Section label row */}
                  <View className="flex-row items-center gap-3">
                    <Text style={{ color: colors.muted, fontSize: 9, letterSpacing: 4 }}>
                      WHO WE ARE
                    </Text>
                    <View style={{ flex: 1, height: 1, backgroundColor: ruleFaint }} />
                  </View>

                  {/* Content: responsive split */}
                  <View className={isSmallScreen ? 'gap-6' : 'flex-row items-center gap-10'}>
                    <View className={`gap-3 ${isSmallScreen ? '' : 'flex-1'}`}>
                      <Text className="text-2xl font-bold" style={{ color: colors.dark }}>
                        {t('about.whoWeAreTitle')}
                      </Text>
                      <Text className="text-sm leading-relaxed" style={{ color: colors.muted }}>
                        {t('about.whoWeAreDesc')}
                      </Text>
                    </View>

                    {/* Social links */}
                    <View
                      className={`flex-row gap-4 ${isSmallScreen ? 'justify-center' : 'justify-end'}`}
                    >
                      <Pressable
                        onPress={() => Linking.openURL('https://www.youtube.com/@otrosmeridianos')}
                        className="items-center gap-2 tbn-social"
                      >
                        <View
                          className="items-center justify-center"
                          style={{
                            width: 96,
                            height: 96,
                            borderRadius: 16,
                            backgroundColor: colors.background,
                            borderWidth: 1,
                            borderColor: ruleMid,
                          }}
                        >
                          <FontAwesome name="youtube-play" size={48} color="#FF0000" />
                        </View>
                        <Text style={{ color: colors.muted, fontSize: 9, letterSpacing: 2 }}>
                          {t('footer.youtube')}
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={() =>
                          Linking.openURL('https://www.instagram.com/otrosmeridianos/')
                        }
                        className="items-center gap-2 tbn-social"
                      >
                        <View
                          className="items-center justify-center"
                          style={{
                            width: 96,
                            height: 96,
                            borderRadius: 16,
                            backgroundColor: colors.background,
                            borderWidth: 1,
                            borderColor: ruleMid,
                          }}
                        >
                          <FontAwesome name="instagram" size={48} color="#C13584" />
                        </View>
                        <Text style={{ color: colors.muted, fontSize: 9, letterSpacing: 2 }}>
                          {t('footer.instagram')}
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                </View>
              </View>

              {/* ─── FEATURE SECTIONS ─────────────────────────────────── */}
              <View className="gap-6">
                {sections.map((section, index) => (
                  <View
                    key={section.title}
                    className="rounded-2xl overflow-hidden tbn-card"
                    style={{
                      backgroundColor: colors.surface,
                      borderWidth: 1,
                      borderColor: ruleMid,
                      flexDirection: isSmallScreen ? 'column' : index === 1 ? 'row-reverse' : 'row',
                    }}
                  >
                    {/* Image */}
                    <View
                      style={{
                        position: 'relative',
                        width: isSmallScreen ? '100%' : 380,
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        source={section.image}
                        style={{
                          width: '100%',
                          height: isSmallScreen ? 360 : '100%',
                          minHeight: isSmallScreen ? undefined : 360,
                        }}
                        resizeMode="cover"
                      />
                      {/* Code badge */}
                      <View
                        style={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          backgroundColor: colors.background,
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6,
                          borderWidth: 1,
                          borderColor: ruleMid,
                        }}
                      >
                        <Text style={{ color: colors.dark, fontSize: 9, letterSpacing: 3 }}>
                          {section.code} · {String(index + 1).padStart(2, '0')}
                        </Text>
                      </View>
                    </View>

                    {/* Separator: bottom on mobile, side on desktop */}
                    <View
                      style={
                        isSmallScreen
                          ? { height: 1, backgroundColor: ruleMid }
                          : { width: 1, backgroundColor: ruleMid }
                      }
                    />

                    {/* Text content */}
                    <View className="p-6 gap-4 flex-1 justify-center">
                      <View className="flex-row items-center gap-3">
                        <Text
                          style={{
                            color: colors.muted,
                            fontSize: 11,
                            letterSpacing: 2,
                            minWidth: 22,
                          }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </Text>
                        <View style={{ width: 16, height: 1, backgroundColor: colors.dark }} />
                        <Text className="text-2xl font-bold flex-1" style={{ color: colors.dark }}>
                          {section.title}
                        </Text>
                      </View>
                      <Text
                        className="text-base leading-relaxed"
                        style={{ color: colors.muted, paddingLeft: 40 }}
                      >
                        {section.description}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>

              {/* ─── FOOTER COPY ──────────────────────────────────────── */}
              <View className="mt-14 items-center gap-4">
                <View className="flex-row items-center gap-3 w-full">
                  <View style={{ flex: 1, height: 1, backgroundColor: ruleFaint }} />
                  <FontAwesome name="plane" size={12} color={colors.muted} />
                  <View style={{ flex: 1, height: 1, backgroundColor: ruleFaint }} />
                </View>
                <View
                  className="items-center gap-2 px-6 py-5 w-full"
                  style={{
                    borderWidth: 1,
                    borderColor: ruleFaint,
                    borderRadius: 12,
                  }}
                >
                  <Text
                    className="text-xs font-bold text-center"
                    style={{ color: colors.dark, letterSpacing: 3 }}
                  >
                    {t('about.freeForUsers')}
                  </Text>
                  <Text
                    className="text-xs text-center leading-relaxed"
                    style={{ color: colors.muted }}
                  >
                    {t('about.freeMission')}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        <Footer showTermsLink />
      </ScrollView>
    </View>
  )
}
